import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL as API } from '@/lib/apiBase';

const emptyForm = {
  name: '',
  email: '',
  password: '',
  date_of_birth: '',
  phone: '',
};

const readApiMessage = (err, fallback) => {
  if (err.response?.status === 404) {
    return 'This account option is not live on the server yet. Please deploy the latest backend and try again.';
  }
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

export default function UserAccountJoinModal({ isOpen, onClose, intent = 'learner' }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isBuyer = intent === 'hiring' || intent === 'buyer';
  const title = isBuyer ? 'Buyer / Hiring Account' : 'Learner Account';
  const learnerLandingPath = typeof window !== 'undefined' && window.location.hostname === 'learn.kalamanch.art' ? '/?section=my-plans' : '/learn?section=my-plans';
  const landingPath = isBuyer
    ? (typeof window !== 'undefined' && window.location.hostname === 'market.kalamanch.art' ? '/' : 'https://market.kalamanch.art')
    : learnerLandingPath;

  useEffect(() => {
    if (!isOpen) return;
    if (isBuyer) {
      window.location.assign('https://market.kalamanch.art/marketplace/account');
      return;
    }
    setMode('login');
    setForm(emptyForm);
    setMessage('');
    setError('');
    setLoading(false);
  }, [isOpen, intent, isBuyer]);

  if (!isOpen || isBuyer) return null;

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
    setMessage('');
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      if (mode === 'signup') {
        await axios.post(
          `${API}/learn/signup`,
          {
            name: form.name,
            email: form.email,
            password: form.password,
            date_of_birth: form.date_of_birth,
            signup_intent: 'learner',
          },
          { withCredentials: true }
        );
        onClose();
        window.dispatchEvent(new Event('learnAuthChanged'));
        navigate(landingPath, { replace: false });
      } else {
        await axios.post(`${API}/learn/login`, { email: form.email, password: form.password }, { withCredentials: true });
        onClose();
        window.dispatchEvent(new Event('learnAuthChanged'));
        navigate(landingPath, { replace: false });
      }
    } catch (err) {
      setError(readApiMessage(err, 'Unable to continue. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-start justify-center overflow-y-auto bg-slate-900/55 px-3 py-4 sm:items-center sm:px-4" onClick={onClose}>
      <div className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-4 shadow-2xl sm:p-6" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#C45A00]">KalaManch Account</p>
            <h3 className="mt-2 text-2xl font-bold text-slate-900">{title}</h3>
            <p className="mt-2 text-sm text-slate-600">
              {isBuyer
                ? 'Use this account for marketplace enquiries. Email verification is required before sending a purchase request.'
                : 'Use your existing KalaManch account for Learn. No email verification is required for lessons.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-50">
            Close
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2 rounded-full bg-slate-100 p-1">
          {['login', 'signup'].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setMode(item);
                setError('');
                setMessage('');
              }}
              className={`rounded-full px-4 py-2 text-sm font-black ${mode === item ? 'bg-[#FF6F00] text-white' : 'text-slate-600'}`}
            >
              {item === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-5 grid gap-3">
          {mode === 'signup' && (
            <>
              <input required value={form.name} onChange={(event) => updateField('name', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6F00]" placeholder="Full name" />
              {isBuyer && (
                <input value={form.phone} onChange={(event) => updateField('phone', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6F00]" placeholder="Phone / WhatsApp" />
              )}
              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700">Date of birth *</label>
                <input required type="date" value={form.date_of_birth} onChange={(event) => updateField('date_of_birth', event.target.value)} className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6F00]" />
              </div>
            </>
          )}
          <input required type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6F00]" placeholder="Email address" />
          <input required type="password" minLength="8" value={form.password} onChange={(event) => updateField('password', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6F00]" placeholder="Password" />

          {message && <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{message}</div>}
          {error && <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{error}</div>}

          <button type="submit" disabled={loading} className="rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-black text-white disabled:opacity-60">
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
}
